import React, {useEffect, useState} from "react";
import {
    Box,
    Grid,
    Typography,
    IconButton,
    FormControl,
    TextField,
    Tooltip,
    Chip,
    Rating,
    Stack,
} from "@mui/material";

import ReplayIcon from '@mui/icons-material/Replay';
import FlipOutlinedIcon from '@mui/icons-material/FlipOutlined';
import StarFilled from "@ant-design/icons/StarFilled";
import StarOutlined from "@ant-design/icons/StarOutlined";
import CustomAutoComplete from "./CommonComponents/CustomAutoComplete";
import { revertKeyMoment, updateKeyMomentSimple } from "@/queries/keyMomentQueries";
import { enqueueSnackbar } from "notistack";
import { updateSimpleLogicClip } from "@/queries/clipQueries";
import {CURRENT_SPORT, SUPPORTED_SPORTS} from "@/constants/globalConstants";
import {useIntl} from "react-intl";
import {getCardTitle} from "@/pages/custom-components/clipCard";
import {getKeyByValueMapping} from "@/utils/locales/translationUtility";
import { iconLabelHoverSx } from "@/styles/iconHover";

/**
 * Back-face content for clip / key-moment cards.
 *
 * Renders inside the orange Card shell provided by WipeCard — no Card wrapper here.
 * `setFlipped` is injected by WipeCard via the renderBack render prop.
 */
export default function ClipCardRetro({ card, clipType, setFlipped, nameEditDisabled, sortable = false }) {
    const [autoCompleteTextValues, setAutoCompleteTextValues] = React.useState({});
    const [minute, setMinute] = React.useState(0);
    const [editingField, setEditingField] = useState(null);
    const [localActionType, setLocalActionType] = useState("");
    const [localPlayer, setLocalPlayer] = useState("");
    const [localRating, setLocalRating] = useState(0);
    const [newTags, setNewTags] = useState([]);
    const [localNote, setLocalNote] = useState("");
    const [title, setTitle] = useState("");
    const intl = useIntl();


    React.useEffect(() => {
        if (minute >= card.current_time) return;
        const interval = setInterval(() => {
            setMinute((prev) =>
                prev >= card.current_time ? card.current_time : prev + 1
            );
        }, 60000);
        return () => clearInterval(interval);
    }, [minute, card.current_time]);

    React.useEffect(() => {
        if (card) {
            setMinute(card.current_time);
            setLocalActionType(card.title || "");
            setLocalPlayer(card.lead || "");
            setNewTags(card.tags || []);
            setLocalRating(card.rating ?? 0);
            setLocalNote(card.notes || "");
            setTitle(clipType === 'clips' ? getCardTitle(card) : (getCardTitle(card) ? intl.formatMessage({id: getCardTitle(card), defaultMessage: getCardTitle(card) }): ''));
        }
    }, []);

    const handleFilter = (nameFilter, values) => {
        if (nameFilter === "tags") {
            if (!values) return;
            enqueueSnackbar("Successfully modified!");
            const uiTags  = values.map((tag) => typeof tag === "string" ? { id: tag, value: tag } : tag);
            setNewTags(uiTags);
            const apiTags = uiTags.map(tag => tag.value);
            try {
                updateKeyMomentSimple(card?.id, nameFilter, apiTags);
            } catch (error) {
                console.error(error);
                enqueueSnackbar("Failed to confirm.");
            }
        }
    };

    function onAutoCompleteTextChange(field, value) {
        setAutoCompleteTextValues((prev) => ({ ...prev, [field]: value }));
    }

    const handleConfirm = (e, id, setValue, field, value) => {
        if (clipType === 'clips') {
            try {
                setValue(value);
                updateSimpleLogicClip(id, {[field]: value});
                enqueueSnackbar("Successfully modified!");
            } catch {
                enqueueSnackbar("Failed to confirm.");
            }
        } else {
            e.stopPropagation();
            try {
                setValue(value);
                if (field === 'title' || field === 'name')
                    updateKeyMomentSimple(id,field, getKeyByValueMapping(value, intl.locale));
                else
                    updateKeyMomentSimple(id, field, value);
                enqueueSnackbar("Successfully confirmed!");
            } catch (error) {
                enqueueSnackbar("Failed to confirm.");
            }
        }
    };

    return (
        <Box sx={{ flex: 1, width: "100%", pt: sortable ? 3 : 0}} data-tour={"dsjkdjkdfjkdfjk"}>
            {/* Header row: Identifier label + Flip / Reset buttons */}
            <Grid container size={{ xs: 12 }} sx={{ width: "100%" }} alignItems="center" justifyContent="space-between">
                <Grid >
                    <Tooltip title="Identifier" placement="bottom">
                        <Typography sx={{ fontSize: "10px", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Identifier
                        </Typography>
                    </Tooltip>
                </Grid>

                <Grid >
                    <Stack direction="row" spacing={1} sx={{ zIndex: 10 }}>
                        <Tooltip title="Flip the Card">
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={0.5}
                                onClick={(e) => { e.stopPropagation(); setFlipped(false); }}
                                sx={iconLabelHoverSx()}
                            >
                                <Typography sx={{ fontSize: '10px', color: '#fff' }}>Flip</Typography>
                                <FlipOutlinedIcon fontSize="small" sx={{ color: '#fff' }} />
                            </Stack>
                        </Tooltip>
                        {card?.revertable && (
                            <Tooltip title="Revert changes">
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={0.5}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        revertKeyMoment(card.id).then(r => {
                                            setTitle(r.type);
                                            setLocalPlayer(r.player);
                                            setLocalRating(r.rating);
                                            setLocalNote(r.notes);
                                            setNewTags(r.tags);
                                        });
                                        enqueueSnackbar("Successfully reverted changes.");
                                    }}
                                    sx={iconLabelHoverSx()}
                                >
                                    <Typography sx={{ fontSize: '10px', color: '#fff' }}>Reset</Typography>
                                    <ReplayIcon fontSize="small" sx={{ color: '#fff' }} />
                                </Stack>
                            </Tooltip>
                        )}
                    </Stack>
                </Grid>
            </Grid>

            <Grid container size={{ xs: 12 }} sx={{ width: "100%" }}>
                {/* Identifier value */}
                <Grid size={{ xs: 12 }}>
                    <Tooltip title={card?.id || ""} placement="bottom">
                        <Typography sx={{ fontSize: "12px", color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {card?.id || "—"}
                        </Typography>
                    </Tooltip>
                </Grid>

                {/* Name */}
                <Grid container size={{ xs: 12 }}>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body1" sx={{ fontSize: '10px', alignContent: "center", height: "100%", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Name:
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        {editingField === "title" ? (
                            <TextField
                                size="small" autoFocus value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={(e) => {
                                    if ((card?.title || "") !== (title || "")) handleConfirm(e, card?.id, setTitle, "title", title);
                                    setEditingField(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        if ((card?.title || "") !== (title || "")) handleConfirm(e, card?.id, setTitle, "title", title);
                                        setEditingField(null);
                                        e.stopPropagation();
                                    }
                                }}
                                sx={{ borderRadius: 2, width: "100%", input: { color: "#fff", fontSize: "0.75rem" }, "& .MuiInput-underline:before": { borderBottomColor: "#fff" }, "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "#fff" }, "& .MuiInput-underline:after": { borderBottomColor: "#fff" } }}
                                variant="standard"
                            />
                        ) : (
                            <Tooltip title={nameEditDisabled ? "Name is locked because the collection filters by key moment type" : ""}>
                                <Chip label={title || "No type"} size="small"
                                    onClick={(e) => { e.stopPropagation(); if (!nameEditDisabled) setEditingField("title"); }}
                                    sx={{ width: "100%", fontSize: "12px", height: 22, color: "#fff", cursor: nameEditDisabled ? "not-allowed" : "pointer", opacity: nameEditDisabled ? 0.6 : 1, backgroundColor: 'transparent', display: "flex", alignItems: "flex-start", justifyContent: 'flex-start', alignContent: 'flex-start' }}
                                />
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>

                {/* Player */}
                <Grid container size={{ xs: 12 }}>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body1" sx={{ fontSize: '10px', alignContent: "center", height: "100%", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Player:
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        {editingField === "lead" && clipType === "key_moments" ? (
                            <TextField
                                size="small" autoFocus value={localPlayer}
                                onChange={(e) => setLocalPlayer(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                                onBlur={(e) => {
                                    if ((card?.lead || "") !== (localPlayer || "")) handleConfirm(e, card?.id, setLocalPlayer, "lead", localPlayer);
                                    setEditingField(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        if ((card?.lead || "") !== (localPlayer || "")) handleConfirm(e, card?.id, setLocalPlayer, "lead", localPlayer);
                                        setEditingField(null);
                                        e.stopPropagation();
                                    }
                                }}
                                sx={{ borderRadius: 2, width: "100%", input: { color: "#fff", fontSize: "0.75rem" }, "& .MuiInput-underline:before": { borderBottomColor: "#fff" }, "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "#fff" }, "& .MuiInput-underline:after": { borderBottomColor: "#fff" } }}
                                variant="standard"
                            />
                        ) : (
                            <Chip label={localPlayer || "—"} size="small"
                                onClick={(e) => { e.stopPropagation(); setEditingField("lead"); }}
                                sx={{ width: "100%", fontSize: "12px", height: 22, color: "#fff", cursor: "pointer", backgroundColor: 'transparent', display: "flex", alignItems: "flex-start", justifyContent: 'flex-start', alignContent: 'flex-start' }}
                            />
                        )}
                    </Grid>
                </Grid>

                {/* Rating */}
                <Grid container size={{ xs: 12 }}>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body1" sx={{ fontSize: "10px", height: "100%", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Rating:
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }} onClick={(e) => e.stopPropagation()} sx={{ display: "flex", alignItems: "center" }}>
                        <Rating
                            size="small"
                            name="simple-controlled"
                            value={localRating}
                            icon={<StarFilled style={{ fontSize: "10px" }} />}
                            emptyIcon={<StarOutlined style={{ fontSize: "10px" }} />}
                            precision={0.1}
                            onChange={(event, value) => {
                                if ((card?.rating !== value) && (clipType === "key_moments"))
                                    handleConfirm(event, card?.id, setLocalRating, "rating", value);
                            }}
                        />
                    </Grid>
                </Grid>

                {/* Tags */}
                <Grid container size={{ xs: 12 }}>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body1" sx={{ fontSize: '10px', height: "100%", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Tags:
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Box
                            sx={{ display: "flex", flexWrap: "wrap", borderRadius: 1, alignItems: "center", backgroundColor: "transparent", p: 0, minHeight: '24px', '& .MuiInputBase-root': { fontSize: '0.75rem', padding: '0px !important', minHeight: '24px' }, '& .MuiInputBase-input': { padding: '0px 4px !important', height: '24px' }, '& .MuiChip-root': { height: '18px', fontSize: '0.65rem', margin: '1px', '& .MuiChip-deleteIcon': { fontSize: '12px' } } }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <FormControl fullWidth size="small">
                                <CustomAutoComplete
                                    name={"tags"}
                                    options={[]}
                                    value={newTags}
                                    disabled={clipType !== 'key_moments'}
                                    onChange={(name, newValue) => (clipType === 'key_moments') && handleFilter("tags", newValue, "multiselect")}
                                    customAutocomplete={true}
                                    textFieldValue={autoCompleteTextValues?.["tags"] || ""}
                                    textFieldOnChange={(value) => (clipType === 'key_moments') && onAutoCompleteTextChange("tags", value)}
                                    multiple={true}
                                    labelField={"value"}
                                    valueField={"id"}
                                    freeSolo={true}
                                    chipVariant="pill"
                                    transparentBackground={true}
                                />
                            </FormControl>
                        </Box>
                    </Grid>
                </Grid>

                {/* Notes */}
                <Grid container size={{ xs: 12 }}>
                    <Grid size={{ xs: 12 }}>
                        <Typography variant="body1" sx={{ fontSize: '10px', alignContent: "center", height: "100%", color: "#fff", overflow: "hidden", textOverflow: "ellipsis" }}>
                            Notes:
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        {editingField === "note" && clipType === "key_moments" ? (
                            <TextField
                                size="small" autoFocus multiline rows={2} value={localNote}
                                onChange={(e) => setLocalNote(e.target.value)}
                                onBlur={(e) => {
                                    if ((card?.note || "") !== (localNote || "")) handleConfirm(e, card?.id, setLocalNote, "notes", localNote || "");
                                    setEditingField(null);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        if ((card?.note || "") !== (localNote || "")) handleConfirm(e, card?.id, setLocalNote, "notes", localNote || "");
                                        setEditingField(null);
                                        e.stopPropagation();
                                    }
                                }}
                                sx={{ borderRadius: 2, width: "100%", input: { color: "#fff", fontSize: "0.75rem" }, "& .MuiInput-underline:before": { borderBottomColor: "#fff" }, "& .MuiInput-underline:hover:not(.Mui-disabled):before": { borderBottomColor: "#fff" }, "& .MuiInput-underline:after": { borderBottomColor: "#fff" } }}
                                variant="standard"
                            />
                        ) : (
                            <Chip label={localNote || "—"} size="small"
                                onClick={(e) => { e.stopPropagation(); setEditingField("note"); }}
                                sx={{ borderRadius: 2, width: "100%", fontSize: "12px", height: 22, color: "#fff", cursor: "pointer", backgroundColor: 'transparent', display: "flex", alignItems: "flex-start", justifyContent: 'flex-start', alignContent: 'flex-start' }}
                            />
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Box>
    );
}
